import { Modal, ModalClose, ModalDialog } from '@mui/joy';
import { Alert, Button } from '@mui/joy';

import { useCallback, useState } from 'react';

import { GroupedDatasetVersionSchemaWithOptionalAsOf } from '@/Pages/Dataset/types';
import { useDatasetVersionMutation } from '@/hooks/useSwr';
import { DATASET_VERSION_FORMATTER } from '@/utils/version-format';

import { TAG_VERSION_DEFAULT } from '../constants';

export function AddDatasetVersionTagModal(props: {
  open: boolean;
  onClose: () => void;
  datasetId: string;
  selected: GroupedDatasetVersionSchemaWithOptionalAsOf;
  mutate: () => void;
}) {
  const [tag, setTag] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { trigger } = useDatasetVersionMutation(props.datasetId);
  const selectedAsOf = props.selected.as_of;
  const selectedTags = props.selected.tags ?? [];
  const hasTags = selectedTags.length > 0;

  const handleAddTag = useCallback(
    (e) => {
      e.preventDefault();
      if (tag !== '') {
        trigger(
          {
            json: {
              as_of: selectedAsOf ?? TAG_VERSION_DEFAULT.as_of,
              tag: tag,
            },
          },
          {
            onSuccess: () => {
              props.mutate();
              setError(null);
              props.onClose();
            },
            onError: (e) => {
              setError(e.message);
            },
          }
        );
      }
    },
    [props, selectedAsOf, tag, trigger]
  );

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <ModalDialog>
        <ModalClose />
        <div className="flex max-w-[400px] flex-col gap-3 p-4">
          <h2 className="text-lg font-semibold">Add Tag</h2>
          <div>
            <div className="text-md line-clamp-1 max-w-[350px] text-ellipsis whitespace-normal break-words break-all font-normal">
              {hasTags
                ? selectedTags.join(', ')
                : selectedAsOf
                ? DATASET_VERSION_FORMATTER.format(new Date(selectedAsOf))
                : TAG_VERSION_DEFAULT.tags.join(', ')}
            </div>
            {hasTags && (
              <div className="line-clamp-1 max-w-[350px] text-ellipsis whitespace-normal break-words break-all text-sm text-tertiary">
                {selectedAsOf &&
                  DATASET_VERSION_FORMATTER.format(new Date(selectedAsOf))}
              </div>
            )}
          </div>
          <form className="flex flex-col gap-4" onSubmit={handleAddTag}>
            <input
              type="text"
              placeholder="Tag Name"
              className="flex-grow rounded-md border border-secondary p-2 dark:bg-black"
              onChange={(e) => setTag(e.target.value)}
            />
            <Button size="sm" color="primary" variant="outlined" type="submit">
              Add
            </Button>
            {error && (
              <Alert variant="outlined" color="danger">
                {error}
              </Alert>
            )}
          </form>
        </div>
      </ModalDialog>
    </Modal>
  );
}
